import React, { useState, useEffect } from "react";
import { Button } from "../Common.jsx";
import { DynamicIcon } from "../icons.jsx";

export default function MfaConfigForm({ data, onSave, onNext }) {

  const [form, setForm] = useState({
    mfa_enabled: data?.mfa_enabled ?? false,
    mfa_methods: Array.isArray(data?.mfa_methods) ? data.mfa_methods : [],
  });

  useEffect(() => {
    setForm({
      mfa_enabled: data?.mfa_enabled ?? false,
      mfa_methods: Array.isArray(data?.mfa_methods) ? data.mfa_methods : [],
    });
  }, [data]);

  const toggleMethod = (method) => {
    const current = form.mfa_methods;
    const updated = current.includes(method)
      ? current.filter(m => m !== method)
      : [...current, method];
    setForm({ ...form, mfa_methods: updated });
  };

  // Removed Authenticator, SMS, WhatsApp as per request.
  const methods = [
    { id: "email", label: "Email OTP", icon: "Mail", desc: "Code sent to registered email" },
  ];

  const handleSave = async () => {
    // Merge with existing data to prevent partial update wiping out other fields
    // since the backend model has defaults that might override missing fields.
    await onSave({
      ...data,
      mfa_enabled: form.mfa_enabled,
      mfa_methods: form.mfa_methods
    });
    if (onNext) onNext();
  };

  return (
    <div className="max-w-3xl space-y-6">

      {/* Master Toggle */}
      <div className={`p-6 rounded-xl border-2 transition-all flex items-center justify-between ${form.mfa_enabled ? 'border-primary bg-primary/5' : 'border-gray-200 dark:border-gray-700'} `}>
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-full ${form.mfa_enabled ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'} `}>
            <DynamicIcon name="Lock" className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Multi-Factor Authentication</h3>
            <p className="text-sm text-gray-500">Require specific auth methods for higher security.</p>
          </div>
        </div>
        <input
          type="checkbox"
          className="toggle toggle-primary toggle-lg"
          checked={form.mfa_enabled}
          onChange={(e) => setForm({ ...form, mfa_enabled: e.target.checked })}
        />
      </div>

      {form.mfa_enabled && (
        <div className="animate-fade-in space-y-4">
          <h4 className="font-semibold text-gray-700 dark:text-gray-300">Allowed Methods</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {methods.map((m) => {
              const active = form.mfa_methods.includes(m.id);
              return (
                <div
                  key={m.id}
                  onClick={() => toggleMethod(m.id)}
                  className={`cursor-pointer p-4 rounded-lg border flex items-center gap-4 transition-all hover:bg-gray-50 dark:hover:bg-gray-800 ${active ? 'border-primary ring-1 ring-primary' : 'border-gray-200 dark:border-gray-700'} `}
                >
                  <div className={`p-2 rounded ${active ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-400'} `}>
                    <DynamicIcon name={m.icon} className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-white">{m.label}</div>
                    <div className="text-xs text-gray-500">{m.desc}</div>
                  </div>
                  {active && <DynamicIcon name="CheckCircle" className="text-primary w-5 h-5" />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="sticky bottom-0 z-10 bg-white/90 dark:bg-gray-800/90 backdrop-blur border-t dark:border-gray-700 p-4 -mx-6 -mb-6 mt-8 flex justify-end shadow-lg rounded-b-xl">
        <Button onClick={handleSave} className="px-6 shadow-md">
          Save & Next <DynamicIcon name="ArrowRight" className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
